/**
 * Landing Page
 *
 * Public marketing page showcasing Arber's arbitrage betting platform.
 * Focuses on arbitrage as the primary value proposition.
 *
 * Features:
 * - Hero section with clear value proposition
 * - Feature highlights
 * - Subscription tiers (Free, Gold, Platinum)
 * - Call-to-action to sign up
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import SubscriptionTierCards, { type SubscriptionTier } from '../components/SubscriptionTierCards';
import BillingPeriodToggle, { type BillingPeriod } from '../components/BillingPeriodToggle';
import SubscriptionBlockedModal from '../components/SubscriptionBlockedModal';
import { useAuth } from '../hooks/useAuth';
import '../styles/LandingPage.css';

export default function LandingPage() {
  const { user, loading } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  const renderLandingCTA = (tier: SubscriptionTier) => {
    // Free tier - allow signup
    if (tier === 'free') {
      return (
        <Link
          to="/login?mode=signup"
          className={`subscription-cta subscription-cta-${tier}`}
        >
          Get Started Free
        </Link>
      );
    }

    // Paid tiers - show blocked modal
    return (
      <button
        type="button"
        className={`subscription-cta subscription-cta-${tier}`}
        onClick={() => setShowBlockedModal(true)}
      >
        Subscribe Now
      </button>
    );
  };

  return (
    <div className="landing-page">
      {/* Navigation Header */}
      <nav className="landing-nav">
        <div className="landing-nav-content">
          <div className="landing-brand">
            <img src="/logo.png" alt="Arber Logo" className="landing-logo" />
            <span className="landing-brand-text">Arber</span>
          </div>
          <div className="landing-nav-links">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            {loading ? (
              <span style={{ color: '#718096' }}>Loading...</span>
            ) : user ? (
              <>
                <Link to="/dashboard" className="nav-link-login">
                  Dashboard
                </Link>
                <Link to="/account" className="nav-link-signup">
                  My Account
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link-login">
                  Log In
                </Link>
                <Link to="/login?mode=signup" className="nav-link-signup">
                  Sign Up Free
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        {/* Floating Bookmaker Logos with Odds */}
        <div className="hero-books-container">
          <div className="hero-book-item hero-book-1">
            <img src="/bookmaker-logos/fanduel.png" alt="FanDuel" className="hero-book-logo" />
            <span className="hero-book-odds">+145</span>
          </div>
          <div className="hero-book-item hero-book-2">
            <img src="/bookmaker-logos/draftkings.png" alt="DraftKings" className="hero-book-logo" />
            <span className="hero-book-odds">-110</span>
          </div>
          <div className="hero-book-item hero-book-3">
            <img src="/bookmaker-logos/betmgm.png" alt="BetMGM" className="hero-book-logo" />
            <span className="hero-book-odds">+175</span>
          </div>
          <div className="hero-book-item hero-book-4">
            <img src="/bookmaker-logos/hardrockbet.png" alt="Hard Rock Bet" className="hero-book-logo" />
            <span className="hero-book-odds">+220</span>
          </div>
          <div className="hero-book-item hero-book-5">
            <img src="/bookmaker-logos/betrivers.png" alt="BetRivers" className="hero-book-logo" />
            <span className="hero-book-odds">-105</span>
          </div>
          <div className="hero-book-item hero-book-6">
            <img src="/bookmaker-logos/ballybet.png" alt="Bally Bet" className="hero-book-logo" />
            <span className="hero-book-odds">-125</span>
          </div>
          <div className="hero-book-item hero-book-7">
            <img src="/bookmaker-logos/bovada.png" alt="Bovada" className="hero-book-logo" />
            <span className="hero-book-odds">+185</span>
          </div>
          <div className="hero-book-item hero-book-8">
            <img src="/bookmaker-logos/fliff.png" alt="Fliff" className="hero-book-logo" />
            <span className="hero-book-odds">+115</span>
          </div>
          <div className="hero-book-item hero-book-9">
            <img src="/bookmaker-logos/fanatics.png" alt="Fanatics" className="hero-book-logo" />
            <span className="hero-book-odds">-140</span>
          </div>
          <div className="hero-book-item hero-book-10">
            <img src="/bookmaker-logos/espnbet.png" alt="ESPN BET" className="hero-book-logo" />
            <span className="hero-book-odds">+165</span>
          </div>
        </div>

        <div className="hero-content">
          <h1 className="hero-title">
            Find <span className="hero-highlight">Guaranteed Profit</span> in Sports Betting
          </h1>
          <p className="hero-subtitle">
            Arber automatically scans 80+ sportsbooks across US, UK, EU, and AU to find arbitrage opportunities
            where you can bet on all outcomes and profit regardless of the result.
          </p>
          <div className="hero-cta">
            {user ? (
              <Link to="/dashboard" className="cta-button cta-primary">
                Go to Dashboard
              </Link>
            ) : (
              <Link to="/login?mode=signup" className="cta-button cta-primary">
                Get Started Free
              </Link>
            )}
            <a href="#features" className="cta-button cta-secondary">
              Learn More
            </a>
          </div>
          {!user && (
            <p className="hero-note">
              Start free with 10 searches per day • No credit card required
            </p>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="features-content">
          <h2 className="section-title">Why Arber?</h2>
          <p className="section-subtitle">
            Everything you need to find guaranteed profits—completely free
          </p>
          <div className="features-grid">
            <div className="feature-card feature-card-compact">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M2 12h20"></path>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
              </div>
              <h3 className="feature-title">80+ Sportsbooks Globally</h3>
              <p className="feature-description">
                Access real-time odds from over 80 sportsbooks across US, UK, EU, and AU markets.
                Find the best lines wherever you are.
              </p>
            </div>
            <div className="feature-card feature-card-books">
              <h3 className="feature-title feature-title-top">All Major US Books</h3>
              <div className="feature-book-logos-large">
                {/* Row 1: Mix of major and medium books */}
                <div className="book-logos-tier">
                  <div className="book-logo-item tier-1">
                    <img src="/bookmaker-logos/fanduel.png" alt="FanDuel" />
                  </div>
                  <div className="book-logo-item tier-2">
                    <img src="/bookmaker-logos/betrivers.png" alt="BetRivers" />
                  </div>
                  <div className="book-logo-item tier-1">
                    <img src="/bookmaker-logos/draftkings.png" alt="DraftKings" />
                  </div>
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/ballybet.png" alt="Bally Bet" />
                  </div>
                </div>
                {/* Row 2: Mix of all tiers */}
                <div className="book-logos-tier">
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/fliff.png" alt="Fliff" />
                  </div>
                  <div className="book-logo-item tier-1">
                    <img src="/bookmaker-logos/betmgm.png" alt="BetMGM" />
                  </div>
                  <div className="book-logo-item tier-2">
                    <img src="/bookmaker-logos/fanatics.png" alt="Fanatics" />
                  </div>
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/betparx.png" alt="BetParx" />
                  </div>
                  <div className="book-logo-item tier-2">
                    <img src="/bookmaker-logos/hardrockbet.png" alt="Hard Rock Bet" />
                  </div>
                </div>
                {/* Row 3: Mix of major and smaller books */}
                <div className="book-logos-tier">
                  <div className="book-logo-item tier-2">
                    <img src="/bookmaker-logos/bovada.png" alt="Bovada" />
                  </div>
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/betonlineag.png" alt="BetOnline" />
                  </div>
                  <div className="book-logo-item tier-1">
                    <img src="/bookmaker-logos/espnbet.png" alt="ESPN BET" />
                  </div>
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/mybookieag.png" alt="MyBookie" />
                  </div>
                </div>
                {/* Row 4: Mix of medium and small books */}
                <div className="book-logos-tier">
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/betus.png" alt="BetUS" />
                  </div>
                  <div className="book-logo-item tier-2">
                    <img src="/bookmaker-logos/williamhill_us.png" alt="William Hill" />
                  </div>
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/lowvig.png" alt="LowVig" />
                  </div>
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/rebet.png" alt="Rebet" />
                  </div>
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/betanysports.png" alt="BetAnySports" />
                  </div>
                </div>
                {/* Row 5: Small books */}
                <div className="book-logos-tier">
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/novig.png" alt="Novig" />
                  </div>
                  <div className="book-logo-item tier-3">
                    <img src="/bookmaker-logos/prophetx.png" alt="ProphetX" />
                  </div>
                </div>
              </div>
              <p className="feature-description">
                Every major US sportsbook integrated and ready to use
              </p>
            </div>
            <div className="feature-card feature-card-compact">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
              </div>
              <h3 className="feature-title">1-Click Betting</h3>
              <p className="feature-description">
                Direct links take you straight to your sportsbook with the bet ready to place.
                No searching, no hassle—just click and bet.
              </p>
            </div>
            <div className="feature-card feature-card-live feature-card-compact">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <h3 className="feature-title feature-title-live">
                <span className="feature-live-badge-inline">
                  <span className="feature-live-dot"></span>
                  <span className="feature-live-text">LIVE</span>
                </span>
                Betting
              </h3>
              <p className="feature-description">
                Find arbitrage opportunities on live games as they happen. Real-time odds
                updates ensure you never miss a profitable moment.
              </p>
            </div>
            <div className="feature-card feature-card-compact">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="20" x2="12" y2="10"></line>
                  <line x1="18" y1="20" x2="18" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="16"></line>
                </svg>
              </div>
              <h3 className="feature-title">+EV Opportunities</h3>
              <p className="feature-description">
                Find positive expected value bets by comparing sportsbook odds with
                consensus probabilities from sharp books. Maximize long-term profit.
              </p>
            </div>
            <div className="feature-card feature-card-free">
              <div className="feature-free-badge">FREE</div>
              <h3 className="feature-title feature-title-large">100% Free to Start</h3>
              <div className="feature-free-stats">
                <div className="feature-free-stat">
                  <div className="feature-free-stat-number">10</div>
                  <div className="feature-free-stat-label">Searches/Day</div>
                </div>
                <div className="feature-free-stat">
                  <div className="feature-free-stat-number">$0</div>
                  <div className="feature-free-stat-label">Forever</div>
                </div>
              </div>
              <p className="feature-description">
                Get started with 10 arbitrage searches per day, unlimited +EV opportunities, and access to all sportsbooks. No credit card required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="how-it-works-content">
          <h2 className="section-title">How It Works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3 className="step-title">Sign Up Free</h3>
              <p className="step-description">
                Create your account in seconds. No credit card required.
              </p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">2</div>
              <h3 className="step-title">Set Your Filters</h3>
              <p className="step-description">
                Choose your sports, bookmakers, and minimum profit percentage.
              </p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">3</div>
              <h3 className="step-title">Find Opportunities</h3>
              <p className="step-description">
                Browse guaranteed profit opportunities updated in real-time.
              </p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">4</div>
              <h3 className="step-title">Place Your Bets</h3>
              <p className="step-description">
                Use our calculator to place optimal bets and lock in guaranteed profit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing">
        <div className="pricing-content">
          <h2 className="section-title">Pricing</h2>
          <p className="section-subtitle">
            Choose the plan that fits your betting strategy.
          </p>

          {/* Billing Period Toggle */}
          <BillingPeriodToggle
            selected={billingPeriod}
            onChange={setBillingPeriod}
          />

          {/* Subscription Tiers */}
          <SubscriptionTierCards
            renderCTA={renderLandingCTA}
            billingPeriod={billingPeriod}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Ready to Start Winning?</h2>
          <p className="cta-subtitle">
            Join thousands of smart bettors who use Arber to find guaranteed profits every day.
          </p>
          <Link to="/login?mode=signup" className="cta-button cta-primary cta-large">
            Get Started Free
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-brand">
              <img src="/logo.png" alt="Arber Logo" className="footer-logo" />
              <span className="footer-brand-text">Arber</span>
            </div>
            <p className="footer-tagline">
              The smarter way to bet on sports.
            </p>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Product</h4>
            <ul className="footer-links">
              <li><a href="#features">Features</a></li>
              <li><a href="#pricing">Pricing</a></li>
              <li><Link to="/login">Sign In</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Legal</h4>
            <ul className="footer-links">
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Support</h4>
            <ul className="footer-links">
              <li><a href="mailto:arberodds@gmail.com">Contact Us</a></li>
              <li><a href="/faq">FAQ</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 Arber. All rights reserved.</p>
          <p className="footer-disclaimer">
            Gambling involves risk. Please bet responsibly. Must be 21+ to use this service.
          </p>
        </div>
      </footer>

      <SubscriptionBlockedModal
        isOpen={showBlockedModal}
        onClose={() => setShowBlockedModal(false)}
      />
    </div>
  );
}


